"use client"

import { Check, X, Shield, Users, Package, Headphones, MapPin, Award } from "lucide-react"
import { CONTACT } from "@/lib/constants"

interface ComparisonItem {
  service: string
  others: boolean
  bigwin: boolean
  icon: React.ReactNode
  description: string
}

const comparisonData: ComparisonItem[] = [
  {
    service: "Vehicle Assurance",
    others: true,
    bigwin: true,
    icon: <Shield className="w-5 h-5" />,
    description: "GPS-enabled vehicles with real-time monitoring"
  },
  {
    service: "Verified Professional Partners",
    others: false,
    bigwin: true,
    icon: <Users className="w-5 h-5" />,
    description: "Background-verified and trained moving experts"
  },
  {
    service: "Safe & Cautious Handling",
    others: false,
    bigwin: true,
    icon: <Package className="w-5 h-5" />,
    description: "Premium packing materials and careful handling"
  },
  {
    service: "Bubble / Foam Wrap",
    others: false,
    bigwin: true,
    icon: <Package className="w-5 h-5" />,
    description: "High-quality protective wrapping for fragile items"
  },
  {
    service: "Furniture Disassembly and Reassembly",
    others: true,
    bigwin: true,
    icon: <Package className="w-5 h-5" />,
    description: "Expert furniture handling and installation"
  },
  {
    service: "On Demand Warehouse Storage",
    others: false,
    bigwin: true,
    icon: <MapPin className="w-5 h-5" />,
    description: "Secure climate-controlled storage facilities"
  },
  {
    service: "Dedicated Customer Support",
    others: false,
    bigwin: true,
    icon: <Headphones className="w-5 h-5" />,
    description: "24/7 customer support via phone, email & chat"
  },
  {
    service: "100% Insurance Coverage",
    others: false,
    bigwin: true,
    icon: <Shield className="w-5 h-5" />,
    description: "Comprehensive insurance for complete peace of mind"
  },
  {
    service: "Real-time Tracking",
    others: false,
    bigwin: true,
    icon: <MapPin className="w-5 h-5" />,
    description: "Live GPS tracking of your belongings"
  },
  {
    service: "ISO Certified Services",
    others: false,
    bigwin: true,
    icon: <Award className="w-5 h-5" />,
    description: "ISO 9001:2015 certified quality standards"
  }
]

export function ServiceComparison() {
  return (
    <section 
      className="relative py-20 sm:py-28 overflow-hidden"
      aria-labelledby="comparison-heading"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-blue-50/30 to-white" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.05),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(249,115,22,0.05),transparent_50%)]" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-orange-100 to-orange-50 border border-orange-200 text-orange-700 font-semibold text-sm mb-6 shadow-sm">
            <span className="text-lg">📊</span>
            Service Comparison
          </div>
          <h2 
            id="comparison-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            <span className="bg-gradient-to-r from-blue-600 to-orange-600 bg-clip-text text-transparent">
              Why Bigwin Outperforms
            </span>
            <br />
            Other Packers & Movers
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Compare our comprehensive services with other movers and discover why 
            <strong className="text-gray-900"> 50,000+ customers</strong> choose Bigwin for their relocation needs.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden backdrop-blur-sm">
            {/* Table Header */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 sm:p-8 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
              
              <div className="text-left relative z-10 hidden md:block">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Services & Features
                </h3>
              </div>
              <div className="text-center relative z-10">
                <div className="inline-flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                    <span className="text-2xl">🏢</span>
                  </div>
                  <h3 className="text-lg font-bold text-white/90">Other Movers</h3>
                  <span className="text-xs text-white/60 font-medium">Standard Services</span>
                </div>
              </div>
              <div className="text-center relative z-10">
                <div className="inline-flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center shadow-lg border-2 border-white/20">
                    <span className="text-2xl">🏆</span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold shadow-lg">
                    Bigwin Packers
                  </div>
                  <span className="text-xs text-orange-200 font-medium">Premium Services</span>
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-100">
              {comparisonData.map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 sm:p-8 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-orange-50/50 transition-all duration-300 group"
                >
                  {/* Service Name */}
                  <div className="flex flex-col gap-2 md:pr-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-orange-100 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="text-gray-900 font-bold text-base sm:text-lg">{item.service}</h4>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 ml-13 hidden sm:block">{item.description}</p>
                  </div>

                  {/* Others Column */}
                  <div className="flex items-center justify-center md:justify-center">
                    <div className="flex items-center gap-3">
                      {item.others ? (
                        <>
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                            <Check className="w-5 h-5 text-green-600" strokeWidth={2.5} />
                          </div>
                          <span className="text-sm font-medium text-gray-600 md:hidden">Available</span>
                        </>
                      ) : (
                        <>
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                            <X className="w-5 h-5 text-red-600" strokeWidth={2.5} />
                          </div>
                          <span className="text-sm font-medium text-gray-600 md:hidden">Not Available</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Bigwin Column */}
                  <div className="flex items-center justify-center md:justify-center">
                    <div className="flex items-center gap-3">
                      {item.bigwin ? (
                        <>
                          <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity" />
                            <div className="relative w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                              <Check className="w-5 h-5 text-white" strokeWidth={3} />
                            </div>
                          </div>
                          <span className="text-sm font-bold text-orange-600 md:hidden">Included ✓</span>
                        </>
                      ) : (
                        <>
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center shadow-sm">
                            <X className="w-5 h-5 text-red-600" strokeWidth={2.5} />
                          </div>
                          <span className="text-sm font-medium text-gray-600 md:hidden">Not Available</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-3 gap-4 p-6 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-400">2-3</div>
                <div className="text-xs text-gray-500 font-medium">Services</div>
              </div>
              <div className="text-center border-l border-r border-gray-200">
                <div className="text-2xl font-bold text-orange-600">10+</div>
                <div className="text-xs text-orange-700 font-bold">Premium Services</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">100%</div>
                <div className="text-xs text-green-700 font-medium">Satisfaction</div>
              </div>
            </div>

            {/* Footer CTA */}
            <div className="relative p-8 sm:p-12 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-center overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium mb-4">
                  <Award className="w-4 h-4" />
                  Trusted by 50,000+ Customers
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                  Experience the Bigwin Difference Today!
                </h3>
                <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                  Get a free, no-obligation quote in just 2 minutes. Our experts are ready to make your move stress-free.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="/get-quote"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-full hover:from-orange-600 hover:to-orange-700 transition-all shadow-xl hover:shadow-2xl hover:scale-105 transform"
                    aria-label="Get free moving quote from Bigwin Packers"
                  >
                    Get Free Quote
                    <span className="text-xl">→</span>
                  </a>
                  <a
                    href={`tel:${CONTACT.phone.primary}`}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white font-bold rounded-full hover:bg-white/20 transition-all"
                    aria-label="Call Bigwin Packers for moving services"
                  >
                    <Headphones className="w-5 h-5" />
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 mb-4">Trusted by leading companies and thousands of families across India</p>
            <div className="flex flex-wrap items-center justify-center gap-6 opacity-60">
              <span className="text-xs font-semibold text-gray-400">ISO 9001:2015 CERTIFIED</span>
              <span className="text-gray-300">•</span>
              <span className="text-xs font-semibold text-gray-400">100% INSURED</span>
              <span className="text-gray-300">•</span>
              <span className="text-xs font-semibold text-gray-400">24/7 SUPPORT</span>
              <span className="text-gray-300">•</span>
              <span className="text-xs font-semibold text-gray-400">500+ CITIES</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
